"use client";

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Archive,
  ArrowUpDown,
  Check,
  ChevronDown,
  CircleAlert,
  Inbox,
  Layers,
  Search,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import {
  AeraImage,
  FilterBar,
  useWideDataLayout,
  type TableFilters,
  type TransactionSummary,
} from "@aera/data-table";
import { SegmentedTabSwitcher } from "@aera/ui";
import { AeraButton } from "@/app/components/ui/AeraButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import {
  batchSetLabel,
  batchHeaderClosedCount,
  batchClosedCount,
  cardBatchClosed,
  cardBatchSize,
  segmentCardsByBatch,
} from "@/lib/financialWork/nav";
import { useFinancialWorkSearch } from "@/app/financial-work/FinancialWorkSearchContext";
import { DataSourcesStatusControl } from "@/app/financial-work/DataSourcesStatusControl";
import { useFinancialWorkWideRail } from "@/lib/financialWork/layout";
import { FinancialWorkSectionHeader } from "@/app/financial-work/FinancialWorkSectionHeader";
import { FinancialWorkPreparingStatus } from "@/app/financial-work/FinancialWorkPreparingStatus";
import { FinancialWorkLoadFailedPanel } from "@/app/financial-work/FinancialWorkViews";
import { useFinancialWorkPreparing } from "@/app/financial-work/FinancialWorkPreparingContext";
import type {
  FinancialWorkFeedItem,
  LiffFinancialWorkCard,
  WorkQueueProjection,
} from "@/lib/financialWork/types";
import { sortWorkQueueCards } from "@/lib/financialWork/feedClient";
import {
  sortWorkQueueCardsByColumn,
  type QueueSortColumn,
  type QueueSortDir,
} from "@/lib/financialWork/queueRowSort";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import {
  fetchAllWorkFeedItems,
  feedTabSlug,
  interpretFeedHttp,
} from "@/lib/financialWork/feedClient";
import {
  computeWorkOverviewFromFeedItems,
  feedItemsToCards,
  getBangkokMonthRangeFromParts,
  getBangkokYearMonth,
} from "@/lib/financialWork/workOverviewTotals";
import {
  createDefaultWorkQueueFilters,
  workQueueCardMatchesFilters,
  workQueueFilterSortColumn,
  workQueueHasClientFilters,
} from "@/lib/financialWork/workQueueCardFilters";
import { WORK_QUEUE_FILTER_SCHEMA } from "@/lib/financialWork/workQueueFilterSchema";
import { INCOME_MONEY_CHANGED_EVENT } from "@/lib/incomeMoneyRefresh";
import {
  EMPTY_TRANSACTION_SUMMARY,
  fetchCombinedWorkspaceTransactionSummary,
} from "@/lib/transactionSummary";
import {
  FinancialWorkQueueRow,
  workQueueCardKey,
  workQueueRowHaystack,
  type QueueFilterId,
} from "@/app/financial-work/FinancialWorkQueueRow";

const marketApiFetch = createMarketApiFetch();

async function loadAllWorkFeedItems(args: {
  workspaceId: string;
  ownerUserId?: string | null;
  actorId: string;
}) {
  return fetchAllWorkFeedItems({
    workspaceId: args.workspaceId,
    ownerUserId: args.ownerUserId,
    actorId: args.actorId,
    fetchPage: async (params) => {
      const qs = new URLSearchParams({
        workspace_id: params.workspaceId,
        limit: String(params.pageSize),
        page: String(Math.max(0, params.page)),
        actor_id: params.actorId,
      });
      if (params.ownerUserId) qs.set("owner_user_id", params.ownerUserId);
      const res = await marketApiFetch(
        `/api/v1/financial-work/feed/${feedTabSlug(params.bucket)}?${qs.toString()}`,
        { cache: "no-store", headers: { Accept: "application/json" } },
      );
      const body = await res.json().catch(() => ({}));
      return interpretFeedHttp(res.status, body);
    },
  });
}

const BILLIN_AVATAR_SRC = "/service_img/cmjcrj5ad000gg8xz8icco4jr.png";

const PAGE_SIZE = 20;

const THAI_MONTHS = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];

const DESKTOP_BUCKETS: Array<{
  id: Exclude<QueueFilterId, "all">;
  label: string;
  icon?: ReactNode;
}> = [
  { id: "needs_action", label: "ต้องให้คุณทำ" },
  {
    id: "waiting",
    label: "BILL-Lin กำลังถือ",
    icon: (
      <AeraImage
        native
        src={BILLIN_AVATAR_SRC}
        alt=""
        className="h-4 w-4 shrink-0 rounded-full object-cover"
      />
    ),
  },
  { id: "recently_closed", label: "ปิดล่าสุด" },
];

function bahtFromMajor(amount: number): string {
  const rounded = Math.round(amount);
  const abs = Math.abs(rounded).toLocaleString("th-TH");
  return rounded < 0 ? `−฿${abs}` : `฿${abs}`;
}

function bahtFromMinor(amountMinor: number): string {
  return bahtFromMajor(amountMinor / 100);
}

/** Months selectable for a year: ม.ค.–เดือนปัจจุบัน for this year, all 12 for past years. */
function overviewMonthOptions(
  year: number,
  now: { year: number; month: number },
): Array<{ value: string; label: string }> {
  const maxMonth =
    year < now.year ? 12 : year > now.year ? 0 : Math.max(0, now.month);
  return THAI_MONTHS.slice(0, maxMonth).map((label, index) => ({
    value: String(index + 1),
    label,
  }));
}

function overviewYearOptions(currentYear: number) {
  const years: number[] = [];
  for (let y = currentYear; y >= currentYear - 4; y -= 1) {
    years.push(y);
  }
  return years.map((year) => ({
    value: String(year),
    label: String(year + 543),
  }));
}

function maxOverviewMonthForYear(
  year: number,
  now: { year: number; month: number },
): number {
  if (year < now.year) return 12;
  if (year > now.year) return 0;
  return now.month;
}

function OverviewPeriodSelect({
  ariaLabel,
  value,
  options,
  onChange,
  testId,
}: {
  ariaLabel: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected =
    options.find((option) => option.value === value) ?? options[0];

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <AeraButton
          type="button"
          variant="outline"
          size="xs"
          fontWeight="semibold"
          className="shrink-0"
          aria-label={ariaLabel}
          aria-expanded={open}
          data-testid={testId}
          iconRight={
            <ChevronDown
              size={14}
              className={`text-aera-gray transition-transform duration-200 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          }
        >
          <span className="tabular-nums">{selected?.label ?? ""}</span>
        </AeraButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="min-w-[7.5rem] rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => onChange(option.value)}
              className="cursor-pointer justify-between gap-3 rounded-lg px-2.5 py-2 text-xs font-semibold text-aera-charcoal"
            >
              <span className="tabular-nums">{option.label}</span>
              {active ? (
                <Check
                  className="h-3.5 w-3.5 shrink-0 text-aera-amber"
                  strokeWidth={3}
                  aria-hidden
                />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const QUEUE_EMPTY: Record<
  QueueFilterId,
  { title: string; body: string; Icon: LucideIcon }
> = {
  needs_action: {
    title: "ไม่มีงานที่ต้องให้คุณทำ",
    body: "เมื่อมีรายการที่ต้องตัดสินใจ จะแสดงในหมวดนี้",
    Icon: CircleAlert,
  },
  waiting: {
    title: "ไม่มีงานที่ BILL-Lin กำลังถือ",
    body: "เมื่อ Bill-Lin รับงานไปถือไว้ รายการจะแสดงที่นี่",
    Icon: Inbox,
  },
  recently_closed: {
    title: "ไม่มีรายการปิดล่าสุด",
    body: "รายการที่ปิดแล้วจะแสดงที่นี่",
    Icon: Archive,
  },
  all: {
    title: "ไม่มีรายการ",
    body: "เมื่อมีงานเงินที่เกี่ยวข้อง จะแสดงที่นี่",
    Icon: Inbox,
  },
};

function WorkQueueCategoryEmpty(props: { filter: QueueFilterId }) {
  const { title, body, Icon } = QUEUE_EMPTY[props.filter];
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-18 text-center md:px-4"
      data-testid="fw-queue-empty"
    >
      {props.filter === "waiting" ? (
        <span className="mb-3 grayscale opacity-50">
          <AeraImage
            native
            src={BILLIN_AVATAR_SRC}
            alt=""
            className="h-14 w-14 rounded-full object-cover"
          />
        </span>
      ) : (
        <Icon
          className="mb-3 h-12 w-12 text-aera-lightgray"
          strokeWidth={1.25}
          aria-hidden
        />
      )}
      <h3 className="mb-1 text-lg font-bold text-aera-charcoal">{title}</h3>
      <p className="max-w-full text-sm leading-relaxed text-aera-gray md:max-w-[400px]">
        {body}
      </p>
    </div>
  );
}

function WorkQueueSearchEmpty(props: { onClear: () => void }) {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-16 text-center"
      data-testid="fw-queue-search-empty"
    >
      <Search
        className="mb-3 h-10 w-10 text-aera-lightgray"
        strokeWidth={1.5}
        aria-hidden
      />
      <h3 className="text-lg font-bold text-aera-charcoal">
        ไม่พบรายการในมุมมองนี้
      </h3>
      <p className="mt-1 text-sm text-aera-gray">
        ลองเปลี่ยนคำค้นหาหรือตัวกรอง
      </p>
      <AeraButton
        type="button"
        variant="outline"
        fontWeight="medium"
        className="mt-5 cursor-pointer"
        onClick={props.onClear}
      >
        ล้างการค้นหาและตัวกรอง
      </AeraButton>
    </div>
  );
}

function WorkQueueSortableHeader(props: {
  label: string;
  column: QueueSortColumn;
  activeColumn: QueueSortColumn | null;
  dir: QueueSortDir;
  onSort: (column: QueueSortColumn) => void;
  align?: "left" | "right";
  className?: string;
}) {
  const isActive = props.activeColumn === props.column;
  return (
    <th
      scope="col"
      onClick={() => props.onSort(props.column)}
      className={[
        "cursor-pointer select-none px-3 py-2.5 transition-colors hover:text-aera-charcoal",
        props.align === "right" ? "text-right" : "text-left",
        props.className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={[
          "group inline-flex items-center gap-1.5",
          props.align === "right" ? "justify-end" : "justify-start",
        ].join(" ")}
      >
        <span>{props.label}</span>
        {isActive ? (
          <span className="font-black text-aera-amber" aria-hidden>
            {props.dir === "asc" ? "↑" : "↓"}
          </span>
        ) : (
          <ArrowUpDown
            className="h-3 w-3 text-aera-amber opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden
          />
        )}
      </div>
    </th>
  );
}

export function sumOutstandingMinor(cards: LiffFinancialWorkCard[]): number {
  return cards.reduce((sum, card) => {
    if (card.outstanding?.remainingAmountMinor != null) {
      return sum + card.outstanding.remainingAmountMinor;
    }
    if (card.amountSummary?.basis === "remaining") {
      return sum + card.amountSummary.primaryAmountMinor;
    }
    return sum;
  }, 0);
}

export function FinancialWorkOverview(props: {
  workspaceId: string | null;
  workspaceName?: string | null;
  serviceId?: string | null;
  ownerUserId?: string | null;
  canonicalRemainingMinor?: number | null;
  overviewRevision?: number;
}) {
  const nowParts = useMemo(() => getBangkokYearMonth(), []);
  const [periodYear, setPeriodYear] = useState(nowParts.year);
  const [periodMonth, setPeriodMonth] = useState(nowParts.month);
  const [bookIncome, setBookIncome] = useState<TransactionSummary>(
    EMPTY_TRANSACTION_SUMMARY,
  );
  const [bookExpense, setBookExpense] = useState<TransactionSummary>(
    EMPTY_TRANSACTION_SUMMARY,
  );
  const [feedItems, setFeedItems] = useState<FinancialWorkFeedItem[]>([]);
  const [monthIncomeMinor, setMonthIncomeMinor] = useState(0);
  const [monthOutMinor, setMonthOutMinor] = useState(0);
  const [outstandingMinor, setOutstandingMinor] = useState(
    props.canonicalRemainingMinor ?? 0,
  );
  const monthLabel = `${THAI_MONTHS[periodMonth - 1] ?? THAI_MONTHS[0]} ${periodYear + 543}`;
  const yearOptions = useMemo(
    () => overviewYearOptions(nowParts.year),
    [nowParts.year],
  );
  const monthOptions = useMemo(
    () => overviewMonthOptions(periodYear, nowParts),
    [periodYear, nowParts],
  );
  const isWideLayout = useWideDataLayout();
  const preparing = useFinancialWorkPreparing();
  const setOverviewReady = preparing?.setOverviewReady;
  const refreshKey = `${props.workspaceId ?? ""}:${props.overviewRevision ?? 0}:${props.canonicalRemainingMinor ?? ""}`;

  const applyMonthTotals = useCallback(
    (
      items: FinancialWorkFeedItem[],
      year: number,
      month: number,
      remaining: number | null | undefined,
    ) => {
      const { from, to } = getBangkokMonthRangeFromParts(year, month);
      const totals = computeWorkOverviewFromFeedItems(items, {
        canonicalRemainingMinor: remaining,
        monthFrom: from,
        monthTo: to,
      });
      setMonthIncomeMinor(totals.monthIncomeMinor);
      setMonthOutMinor(totals.monthOutMinor);
      setOutstandingMinor(totals.outstandingMinor);
    },
    [],
  );

  useEffect(() => {
    setOutstandingMinor(props.canonicalRemainingMinor ?? 0);
  }, [props.canonicalRemainingMinor]);

  useEffect(() => {
    const maxMonth = maxOverviewMonthForYear(periodYear, nowParts);
    if (maxMonth >= 1 && periodMonth > maxMonth) {
      setPeriodMonth(maxMonth);
    }
  }, [periodYear, periodMonth, nowParts]);

  useEffect(() => {
    const ws = props.workspaceId?.trim();
    if (!ws) {
      setFeedItems([]);
      setOverviewReady?.(true);
      return;
    }
    const actorId = getUserIdFromAuthToken() ?? "anonymous";
    let cancelled = false;
    setOverviewReady?.(false);
    void Promise.all([
      fetchCombinedWorkspaceTransactionSummary(ws),
      loadAllWorkFeedItems({
        workspaceId: ws,
        ownerUserId: props.ownerUserId,
        actorId,
      }),
    ])
      .then(([book, items]) => {
        if (cancelled) return;
        setBookIncome(book.income);
        setBookExpense(book.expense);
        setFeedItems(items);
        setOverviewReady?.(true);
      })
      .catch(() => {
        if (cancelled) return;
        setOverviewReady?.(true);
      });
    return () => {
      cancelled = true;
      // Unmount (tab switch / loading placeholder) must not leave the cover waiting.
      setOverviewReady?.(true);
    };
  }, [props.workspaceId, props.ownerUserId, refreshKey, setOverviewReady]);

  useEffect(() => {
    applyMonthTotals(
      feedItems,
      periodYear,
      periodMonth,
      props.canonicalRemainingMinor,
    );
  }, [
    applyMonthTotals,
    feedItems,
    periodYear,
    periodMonth,
    props.canonicalRemainingMinor,
  ]);

  useEffect(() => {
    const ws = props.workspaceId?.trim();
    if (!ws) return;
    const onMoneyChanged = () => {
      const actorId = getUserIdFromAuthToken() ?? "anonymous";
      void Promise.all([
        fetchCombinedWorkspaceTransactionSummary(ws),
        loadAllWorkFeedItems({
          workspaceId: ws,
          ownerUserId: props.ownerUserId,
          actorId,
        }),
      ]).then(([book, items]) => {
        setBookIncome(book.income);
        setBookExpense(book.expense);
        setFeedItems(items);
      });
    };
    window.addEventListener(INCOME_MONEY_CHANGED_EVENT, onMoneyChanged);
    return () => {
      window.removeEventListener(INCOME_MONEY_CHANGED_EVENT, onMoneyChanged);
    };
  }, [props.workspaceId, props.ownerUserId]);

  const bookBalance = bookIncome.totalAmount - bookExpense.totalAmount;
  const tiles = [
    {
      label: "เงินตามสมุด",
      value: bahtFromMajor(bookBalance),
      hint: "ยอดเงินสดที่บันทึกแล้ว",
    },
    {
      label: "ลูกค้ายังค้าง",
      value: bahtFromMinor(outstandingMinor),
      hint: "ยอดค้างตาม BillLin",
      testId: "fw-canonical-outstanding",
    },
    {
      label: `เงินเข้า · ${monthLabel}`,
      value: `+${bahtFromMajor(monthIncomeMinor / 100)}`,
      hint: "รวมตามคอลัมน์เงินเข้าในตาราง",
      tone: "plus" as const,
    },
    {
      label: `เงินออก · ${monthLabel}`,
      value: `−${bahtFromMajor(Math.abs(monthOutMinor / 100))}`,
      hint: "ไม่รวม invoice ที่ยังไม่จ่าย",
      tone: "minus" as const,
    },
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="fw-work-overview">
      <FinancialWorkSectionHeader
        wide={isWideLayout}
        eyebrow="ภาพรวมการเงิน"
        eyebrowIcon={<Sparkles className="h-3.5 w-3.5" aria-hidden />}
        title="งานเงิน"
        description="ดูเงินที่เกิดขึ้นแล้ว และจัดการเฉพาะเรื่องที่ยังต้องใช้คำตอบจากคุณ"
        status={
          <DataSourcesStatusControl
            workspaceId={props.workspaceId}
            workspaceName={props.workspaceName}
            serviceId={props.serviceId}
            size={isWideLayout ? "sm" : "md"}
          />
        }
      />

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-end gap-2">
          <span className="text-xs font-medium text-aera-gray">รอบเดือน</span>
          <OverviewPeriodSelect
            ariaLabel="เลือกเดือน"
            value={String(periodMonth)}
            options={monthOptions}
            onChange={(next) => setPeriodMonth(Number(next))}
            testId="fw-overview-month-select"
          />
          <OverviewPeriodSelect
            ariaLabel="เลือกปี"
            value={String(periodYear)}
            options={yearOptions}
            onChange={(next) => {
              const year = Number(next);
              setPeriodYear(year);
              const maxMonth = maxOverviewMonthForYear(year, nowParts);
              if (maxMonth >= 1 && periodMonth > maxMonth) {
                setPeriodMonth(maxMonth);
              }
            }}
            testId="fw-overview-year-select"
          />
        </div>

        <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-aera-lightgray min-[900px]:grid-cols-4">
          {tiles.map((item, index) => (
            <div
              key={item.label}
              data-testid={"testId" in item ? item.testId : undefined}
              className={[
                "min-w-0 bg-aera-white px-3 py-3 min-[1024px]:px-4",
                index % 2 === 0 ? "border-r border-aera-lightgray" : "",
                index < 2
                  ? "border-b border-aera-lightgray min-[900px]:border-b-0"
                  : "",
                "min-[900px]:border-r min-[900px]:border-aera-lightgray min-[900px]:last:border-r-0",
              ].join(" ")}
            >
              <p className="text-xs font-medium text-aera-gray">{item.label}</p>
              <p
                className={`mt-1 text-lg font-bold tabular-nums ${
                  item.tone === "plus"
                    ? "text-aera-green"
                    : item.tone === "minus"
                      ? "text-aera-red"
                      : "text-aera-charcoal"
                }`}
              >
                {item.value}
              </p>
              <p className="mt-0.5 text-[11px] text-aera-gray">{item.hint}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function FinancialWorkDesktopQueue(props: {
  projection: WorkQueueProjection;
  workspaceId: string | null;
  workspaceName?: string | null;
  serviceId?: string | null;
  ownerUserId?: string | null;
  filter: QueueFilterId;
  onFilterChange: (filter: QueueFilterId) => void;
  onOpenCard: (card: LiffFinancialWorkCard) => void;
  pageIndex?: number;
  onPageChange?: (page: number) => void;
  pageLoading?: boolean;
  onPageRetry?: () => void;
  pageError?: string | null;
  countsLoading?: boolean;
}) {
  const { projection, filter } = props;
  const isWideLayout = useWideDataLayout();
  /** ≥1440: shell locks to viewport. ≥900: list card fills remaining height. */
  const fillShellHeight = useFinancialWorkWideRail();
  const fillTableHeight = isWideLayout;
  const fwSearch = useFinancialWorkSearch();
  const [localPageIndex, setLocalPageIndex] = useState(0);
  const [localQuery, setLocalQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<QueueSortColumn | null>(null);
  const [sortDir, setSortDir] = useState<QueueSortDir>("desc");
  const query = fwSearch?.searchQuery ?? localQuery;
  const setQuery = fwSearch?.setSearchQuery ?? setLocalQuery;
  const [queueFilters, setQueueFilters] = useState<TableFilters>(
    createDefaultWorkQueueFilters,
  );
  const [clientFilterCards, setClientFilterCards] = useState<
    LiffFinancialWorkCard[] | null
  >(null);
  const [clientFilterLoading, setClientFilterLoading] = useState(false);
  const hasClientFilters = workQueueHasClientFilters(queueFilters);

  useEffect(() => {
    const nextQ = query.trim() || null;
    setQueueFilters((prev) => {
      if ((prev.q ?? null) === nextQ) return prev;
      return { ...prev, q: nextQ };
    });
  }, [query]);

  useEffect(() => {
    const col = workQueueFilterSortColumn(queueFilters.sort);
    const dir: QueueSortDir = queueFilters.dir === "asc" ? "asc" : "desc";
    if (col) {
      setSortColumn(col);
      setSortDir(dir);
      return;
    }
    if (!queueFilters.sort || queueFilters.sort === "record_date") {
      setSortColumn(queueFilters.sort ? "date" : null);
      setSortDir(dir);
    }
  }, [queueFilters.sort, queueFilters.dir]);

  useEffect(() => {
    const ws = props.workspaceId?.trim();
    if (!hasClientFilters || !ws) {
      setClientFilterCards(null);
      setClientFilterLoading(false);
      return;
    }
    let cancelled = false;
    setClientFilterLoading(true);
    const actorId = getUserIdFromAuthToken() ?? "anonymous";
    void loadAllWorkFeedItems({
      workspaceId: ws,
      ownerUserId: props.ownerUserId,
      actorId,
    })
      .then((items) => {
        if (cancelled) return;
        setClientFilterCards(feedItemsToCards(items));
      })
      .catch(() => {
        if (cancelled) return;
        setClientFilterCards([]);
      })
      .finally(() => {
        if (!cancelled) setClientFilterLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasClientFilters, props.workspaceId, props.ownerUserId]);
  const serverPaged = Boolean(projection.page);
  const pageIndex = serverPaged
    ? (props.pageIndex ?? projection.page?.index ?? 0)
    : localPageIndex;
  const setPageIndex = props.onPageChange ?? setLocalPageIndex;

  const setQueueFilter = useCallback(
    (key: string, value: string | null) => {
      setQueueFilters((prev) => ({ ...prev, [key]: value }));
      if (key === "q") {
        setQuery(value ?? "");
      } else {
        setPageIndex(0);
      }
    },
    [setQuery, setPageIndex],
  );

  const pageStale =
    serverPaged &&
    !props.pageLoading &&
    ((projection.page?.index ?? 0) !== pageIndex ||
      (projection.page?.bucket != null && projection.page.bucket !== filter));
  const listLoading = Boolean(
    props.pageLoading || pageStale || (hasClientFilters && clientFilterLoading),
  );
  const tabCountsLoading = Boolean(
    props.countsLoading ||
      pageStale ||
      (hasClientFilters && clientFilterLoading),
  );

  /** All cards matching FilterBar (+ search) across buckets — drives filtered tab badges. */
  const filteredMatchingCards = useMemo(() => {
    if (!hasClientFilters) return null;
    if (!clientFilterCards) return null;
    let filtered = clientFilterCards.filter((card) =>
      workQueueCardMatchesFilters(card, queueFilters),
    );
    const q = query.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((card) =>
        workQueueRowHaystack(card, "all").includes(q),
      );
    }
    return filtered;
  }, [clientFilterCards, hasClientFilters, query, queueFilters]);

  const filteredTabCounts = useMemo(() => {
    if (!filteredMatchingCards) return null;
    const counts = {
      needs_action: 0,
      waiting: 0,
      recently_closed: 0,
      all: 0,
    };
    for (const card of filteredMatchingCards) {
      const bucket = card.workPlacement?.bucket;
      if (
        bucket === "needs_action" ||
        bucket === "waiting" ||
        bucket === "recently_closed"
      ) {
        counts[bucket] += 1;
      }
      counts.all += 1;
    }
    return counts;
  }, [filteredMatchingCards]);

  const buckets = useMemo(() => {
    const byId = new Map(projection.buckets.map((bucket) => [bucket.id, bucket]));
    const tabCounts = filteredTabCounts ?? projection.tabCounts;
    return DESKTOP_BUCKETS.map((meta) => {
      const hit = byId.get(meta.id);
      const count =
        tabCounts?.[meta.id] ?? hit?.count ?? 0;
      return {
        ...meta,
        count,
        cards: hit?.cards ?? [],
      };
    });
  }, [filteredTabCounts, projection.buckets, projection.tabCounts]);

  const totalCards =
    filteredTabCounts?.all ??
    projection.tabCounts?.all ??
    buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  const closedView = filter === "recently_closed";
  const visibleCards = useMemo(() => {
    const sortCards = (cards: LiffFinancialWorkCard[]) => {
      if (sortColumn) {
        return sortWorkQueueCardsByColumn(
          cards,
          sortColumn,
          sortDir,
          closedView,
        );
      }
      return sortWorkQueueCards(cards);
    };

    let baseCards: LiffFinancialWorkCard[];
    if (hasClientFilters) {
      if (!filteredMatchingCards) {
        baseCards = [];
      } else if (filter === "all") {
        baseCards = filteredMatchingCards;
      } else {
        baseCards = filteredMatchingCards.filter(
          (card) => card.workPlacement?.bucket === filter,
        );
      }
      return sortCards(baseCards);
    }

    if (serverPaged) {
      baseCards = pageStale ? [] : (projection.pageCards ?? []);
    } else {
      baseCards =
        filter === "all"
          ? buckets.flatMap((bucket) => bucket.cards)
          : buckets.find((bucket) => bucket.id === filter)?.cards ?? [];
    }

    let filtered = baseCards.filter((card) =>
      workQueueCardMatchesFilters(card, queueFilters),
    );

    if (!serverPaged) {
      const q = query.trim().toLowerCase();
      if (q) {
        filtered = filtered.filter((card) =>
          workQueueRowHaystack(card, filter).includes(q),
        );
      }
    }

    return sortCards(filtered);
  }, [
    buckets,
    closedView,
    filter,
    filteredMatchingCards,
    hasClientFilters,
    pageStale,
    projection.pageCards,
    query,
    queueFilters,
    serverPaged,
    sortColumn,
    sortDir,
  ]);

  const pageSize = projection.page?.size ?? PAGE_SIZE;
  const useLocalPaging = !serverPaged || hasClientFilters;
  const totalForPage = useLocalPaging
    ? visibleCards.length
    : (projection.page?.total ?? 0);
  const pageCount = Math.max(1, Math.ceil(totalForPage / pageSize));
  const safePage = Math.min(pageIndex, pageCount - 1);
  const pageStart = safePage * pageSize;
  const pageRows = useLocalPaging
    ? visibleCards.slice(pageStart, pageStart + pageSize)
    : visibleCards;
  // Server pages may expand batch mates into extra visual rows — range uses the
  // logical window against total, not expanded pageRows.length (avoids 21–46/27).
  const rangeEnd = useLocalPaging
    ? pageStart + pageRows.length
    : Math.min(totalForPage, pageStart + pageSize);
  const pageLoadFailed = Boolean(
    serverPaged &&
      !hasClientFilters &&
      props.onPageRetry &&
      !listLoading &&
      pageRows.length === 0,
  );
  const pageSegments = useMemo(
    () =>
      segmentCardsByBatch(pageRows, {
        closedView: filter === "recently_closed",
        allView: filter === "all",
      }),
    [filter, pageRows],
  );

  useEffect(() => {
    if (serverPaged && !hasClientFilters) return;
    setLocalPageIndex(0);
    if (hasClientFilters) {
      setPageIndex(0);
    }
  }, [filter, query, serverPaged, hasClientFilters, queueFilters, setPageIndex]);

  const handleSort = (column: QueueSortColumn) => {
    const nextDir: QueueSortDir =
      sortColumn === column && sortDir === "desc" ? "asc" : "desc";
    if (sortColumn === column) {
      setSortDir(nextDir);
    } else {
      setSortColumn(column);
      setSortDir("desc");
    }
    const filterSortKey =
      column === "date"
        ? "record_date"
        : column === "money_in"
          ? "money_in"
          : column === "money_out"
            ? "money_out"
            : "related";
    setQueueFilters((prev) => ({
      ...prev,
      sort: filterSortKey,
      dir: sortColumn === column ? nextDir : "desc",
    }));
    setPageIndex(0);
  };

  const hasSearch =
    query.trim().length > 0 || workQueueHasClientFilters(queueFilters);
  const clearSearchAndFilters = () => {
    setQuery("");
    setQueueFilters(createDefaultWorkQueueFilters());
    props.onFilterChange("all");
  };

  const rangeLabel = listLoading
    ? "กำลังโหลด..."
    : pageRows.length === 0
      ? "ไม่มีรายการ"
      : `แสดง ${pageStart + 1}–${rangeEnd} จาก ${totalForPage}`;

  return (
    <div
      className={
        fillShellHeight
          ? "flex min-h-0 min-w-0 flex-1 flex-col"
          : fillTableHeight
            ? "flex min-h-full min-w-0 flex-1 flex-col"
            : "flex shrink-0 flex-col gap-5"
      }
      data-testid="fw-queue"
    >
      <div
        className={
          isWideLayout
            ? "shrink-0 px-4 pb-4 pt-4 min-[900px]:px-6 min-[900px]:pt-5"
            : "shrink-0"
        }
      >
        <FinancialWorkOverview
          workspaceId={props.workspaceId}
          workspaceName={props.workspaceName}
          serviceId={props.serviceId}
          ownerUserId={props.ownerUserId}
          canonicalRemainingMinor={
            projection.canonicalTruth?.remainingAmountMinor
          }
          overviewRevision={projection.projectionRevision}
        />
      </div>

      <section
        className={
          fillTableHeight
            ? "mx-4 mb-4 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-aera-lightgray bg-aera-white min-[900px]:mx-6 min-[900px]:mb-6"
            : "flex min-w-0 shrink-0 flex-col overflow-visible rounded-2xl border border-aera-lightgray"
        }
      >
        <div className="flex shrink-0 items-start justify-between gap-3 px-4 pt-4">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-aera-charcoal">
              รายการเงินและงานที่เกี่ยวข้อง
            </h2>
            <p className="mt-0.5 text-xs text-aera-gray">
              รายการเดียวกันจะอัปเดตสถานะอยู่ที่เดิมหลังคุณตัดสินใจ
            </p>
          </div>
        </div>

        <div
          className="mt-3 flex min-w-0 shrink-0 items-stretch border-b border-aera-lightgray bg-aera-white px-4 pt-3"
          data-testid="fw-queue-filters"
          aria-busy={tabCountsLoading || undefined}
        >
          <SegmentedTabSwitcher
            tabs={[
              ...buckets.map((bucket) => ({
                id: bucket.id,
                label: bucket.label,
                count: tabCountsLoading ? undefined : bucket.count,
                countLoading: tabCountsLoading,
                icon: QUEUE_EMPTY[bucket.id].Icon,
                iconNode: bucket.icon,
              })),
              {
                id: "all" as const,
                label: "ทั้งหมด",
                count: tabCountsLoading ? undefined : totalCards,
                countLoading: tabCountsLoading,
                icon: QUEUE_EMPTY.all.Icon,
              },
            ]}
            activeTab={filter}
            onChange={props.onFilterChange}
            ariaLabel="กรองคิวงานเงิน"
            className="h-full"
          />
        </div>

        {/* Same FilterBar as รายรับ / รายจ่าย — header search on wide; in-list search on mobile. */}
        <FilterBar
          schema={WORK_QUEUE_FILTER_SCHEMA}
          filters={queueFilters}
          setFilter={setQueueFilter}
          hideSearch={isWideLayout}
          rangeLabel={isWideLayout ? rangeLabel : undefined}
          data-testid="fw-queue-filter-bar"
        />

        {pageRows.length === 0 ? (
          listLoading ? (
            <div
              className="flex min-h-0 flex-1 items-center justify-start px-4 py-20"
              data-testid="fw-queue-list-preparing"
              role="status"
              aria-live="polite"
              aria-busy="true"
              aria-label="กำลังเตรียมข้อมูล"
            >
              <FinancialWorkPreparingStatus
                title="กำลังเตรียมข้อมูล"
                description="Bill-Lin กำลังรวบรวมรายการล่าสุดให้คุณ"
              />
            </div>
          ) : pageLoadFailed ? (
            <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
              <FinancialWorkLoadFailedPanel
                onRetry={props.onPageRetry ?? (() => {})}
                title={props.pageError ?? undefined}
              />
            </div>
          ) : hasSearch ? (
            <WorkQueueSearchEmpty onClear={clearSearchAndFilters} />
          ) : (
            <WorkQueueCategoryEmpty filter={filter} />
          )
        ) : (
          <>
        <div className="min-[900px]:hidden">
          {pageSegments.map((segment, segmentIndex) => {
            const inSet = Boolean(segment.batchId);
            const closedCount = batchHeaderClosedCount(segment, {
              inferHiddenClosed: filter === "waiting",
            });
            return (
              <div
                key={
                  segment.batchId
                    ? `batch:${segment.batchId}:${segmentIndex}`
                    : `row:${segmentIndex}:${workQueueCardKey(segment.cards[0]!)}`
                }
                data-testid={segment.batchId ? "fw-batch-set" : undefined}
              >
                {inSet ? (
                  <div
                    data-testid="fw-batch-set-header"
                    className="flex items-center gap-1.5 px-3 pt-2.5 text-[11px] font-semibold text-aera-amberdark"
                  >
                    <Layers className="h-3.5 w-3.5" aria-hidden />
                    {batchSetLabel(segment.memberCount, closedCount)}
                  </div>
                ) : null}
                {segment.cards.map((card, cardIndex) => (
                  <FinancialWorkQueueRow
                    key={`${segmentIndex}:${cardIndex}:${workQueueCardKey(card)}`}
                    card={card}
                    filter={filter}
                    variant="mobile"
                    grouped={inSet}
                    workspaceId={props.workspaceId}
                    onOpen={props.onOpenCard}
                  />
                ))}
              </div>
            );
          })}
        </div>

        <div
          className={
            fillTableHeight
              ? "relative flex min-h-0 flex-1 flex-col overflow-x-auto overflow-y-auto custom-scrollbar bg-aera-white max-[899px]:hidden"
              : "shrink-0 overflow-x-auto max-[899px]:hidden"
          }
        >
          <table className="w-full min-w-[64rem] table-fixed border-collapse text-left text-sm">
            <colgroup>
              <col style={{ width: "12%" }} />
              <col style={{ width: "36%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "13%" }} />
            </colgroup>
            <thead className="sticky top-0 z-5 text-xs font-medium text-aera-gray">
              <tr className="border-b border-aera-lightgray bg-aera-white/90 backdrop-blur-md">
                <WorkQueueSortableHeader
                  label="วันที่"
                  column="date"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={handleSort}
                  className="h-10 whitespace-nowrap text-[11px] font-bold uppercase tracking-wider text-aera-lightslate"
                />
                <th className="px-3 py-2.5 text-left whitespace-nowrap">รายการ</th>
                <WorkQueueSortableHeader
                  label="เงินเข้า"
                  column="money_in"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={handleSort}
                  className="whitespace-nowrap"
                />
                <WorkQueueSortableHeader
                  label="เงินออกจริง"
                  column="money_out"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={handleSort}
                  className="whitespace-nowrap"
                />
                <WorkQueueSortableHeader
                  label="ยอดที่เกี่ยวข้อง"
                  column="related"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={handleSort}
                  className="whitespace-nowrap"
                />
                <th className="px-3 py-2.5 text-left whitespace-nowrap">สถานะ</th>
              </tr>
            </thead>
            {pageSegments.map((segment, segmentIndex) => {
              const inSet = Boolean(segment.batchId);
              const closedCount = batchHeaderClosedCount(segment, {
              inferHiddenClosed: filter === "waiting",
            });
              return (
                <tbody
                  key={
                    segment.batchId
                      ? `batch:${segment.batchId}:${segmentIndex}`
                      : `row:${segmentIndex}:${workQueueCardKey(segment.cards[0]!)}`
                  }
                  data-testid={segment.batchId ? "fw-batch-set" : undefined}
                >
                  {inSet ? (
                    <tr data-testid="fw-batch-set-header">
                      <td
                        colSpan={6}
                        className="px-3 py-1.5 text-[11px] font-semibold text-aera-amberdark"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <Layers className="h-3.5 w-3.5" aria-hidden />
                          {batchSetLabel(segment.memberCount, closedCount)}
                        </span>
                      </td>
                    </tr>
                  ) : null}
                  {segment.cards.map((card, cardIndex) => (
                    <FinancialWorkQueueRow
                      key={`${segmentIndex}:${cardIndex}:${workQueueCardKey(card)}`}
                      card={card}
                      filter={filter}
                      variant="desktop"
                      grouped={inSet}
                      workspaceId={props.workspaceId}
                      onOpen={props.onOpenCard}
                    />
                  ))}
                </tbody>
              );
            })}
          </table>
        </div>
          </>
        )}

        {!listLoading && (
        <div className="shrink-0 border-t border-aera-lightgray bg-aera-white backdrop-blur-md rounded-b-2xl">
          <div className="flex items-center justify-between gap-3 px-3 py-3 text-[11px] font-bold tracking-[0.08em] uppercase text-aera-gray md:px-4">
            <div className="flex items-center gap-2">
              <span>{`หน้า ${safePage + 1}`}</span>
              <span className="text-aera-gray/70">{`/ ${pageCount}`}</span>
              <span className="text-aera-gray/70">{`(แสดง ${pageSize}/หน้า)`}</span>
            </div>
            <div className="flex items-center gap-2">
              <AeraButton
                variant="outline"
                size="xs"
                fontWeight="bold"
                textSize="xs"
                disabled={safePage <= 0}
                onClick={() => setPageIndex(Math.max(0, safePage - 1))}
                className="uppercase"
              >
                ก่อนหน้า
              </AeraButton>
              <AeraButton
                variant="outline"
                size="xs"
                fontWeight="bold"
                textSize="xs"
                disabled={safePage + 1 >= pageCount}
                onClick={() => setPageIndex(safePage + 1)}
                className="uppercase"
              >
                ถัดไป
              </AeraButton>
            </div>
          </div>
        </div>
        )}
      </section>
    </div>
  );
}
